<template>
  <v-col
    v-if="$vuetify.breakpoint.name == 'xs'"
    cols="12"
    class="pa-0 align-center"
    style="height: 100vh;display: flex;margin-top: -70px;"
  >

    <h2 class="text-center">
      <v-icon x-large class="mb-5">mdi-phone-rotate-landscape</v-icon>
      <br />
      Put your phone in landscape mode
    </h2>
  </v-col>
  <v-container
    v-else
    fill-height
    fluid
    grid-list-xl
  >
    <div class="operators">
      <v-data-iterator
        :items="users"
        :search="searchTerm"
        :custom-filter="searchFilter"
        :footer-props="footerProps"
        :loading="isLoading"
        loading-text=""
        item-key="user_id"
      >
        <template #header>
          <div class="header">
            <div class="title">Operators</div>
            <div class="actions">
              <role
                v-if="isPermissionEditingAllowed"
                :entities-by-id="entitiesById"
                :can-view-role-history="canViewRoleHistory"
                @history="showHistory = { for: $event, type: 'role' }"
              />
              <v-btn color="primary" :loading="isLoading" @click="onAddUserClick">Add Operator</v-btn>

              <v-text-field
                v-bind:value="searchTerm"
                v-on:input="updateSearch($event)"
                append-icon="mdi-magnify"
                label="Search"
                hide-details
                single-line
                clearable
                class="pt-0 mt-0 mx-4"
                style="float: right; max-width: 300px"
              />

              <v-select
                v-model="baseProjectIdToSearch"
                :items="baseProjectFilterOptions"
                label="Base project"
                class="pt-0 mt-0 mx-4"
                @input="currentPage = 1"
                style="float: right; max-width: 300px"
              />
            </div>
          </div>
          <v-progress-linear
            indeterminate
            color="green"
            v-if="isLoading"
          />
        </template>
        <template #default="{ items }">
          <operator
            v-for="user in items"
            :key="user.user_id"
            :operator="user"
            :entities-by-id="entitiesById"
            :permissions-by-group="permissionsByGroup"
            :readonly="!isPermissionEditingAllowed || isLoading"
            :can-view-user-history="canViewUserHistory"
            @update-permissions="onUpdatePermissions(user, $event)"
            @update-projects="onUpdateProjects(user, $event)"
            @update-status="onUpdateStatus(user, $event)"
            @edit="openUser(user)"
            @history="showHistory = { for: user.user_id, type: 'user' }"
          />
        </template>
      </v-data-iterator>
    </div>

    <drawer-dialog
      v-model="user.dialog"
      width="70%"
      max-width="1200px"
      :is-loading="isLoading"
    >
      <operator-form-container
        v-if="user.dialog"
        :operator="user.info"
        :entities-by-id="entitiesById"
        :permissions-by-group="permissionsByGroup"
        :roles-by-id="rolesById"
        :is-loading="isLoading"
        :can-edit-transaction-limits="isTransactionLimitEditingAllowed"
        :can-edit-payout-limits="isPayoutLimitEditingAllowed"
        :readonly="!isPermissionEditingAllowed || isLoading"
        @close="user.dialog = false"
        @save="save($event)"
      />
    </drawer-dialog>

    <v-dialog
      v-model="historyDialogModel"
      max-width="1200px"
    >
      <history-view
        :target-id="showHistory.for"
        :entity-type="showHistory.type"
        :users="users"
        :roles-by-id="rolesById"
      />
    </v-dialog>
  </v-container>
</template>

<style lang="scss">
  .header {
    .title {
      font-size: 1.25rem;
    }

    .actions {
      padding: 1rem 0;

      > .v-btn {
        margin-right: 0.5rem;
      }
    }
  }

  .operators {
    width: 100%;
    padding: 0.25rem;
  }

  .operators-entities_select .v-text-field__prefix {
    padding-right: 10px;
  }

  .operators-entities_select .v-select__selection {
    margin: 0 7px 0 0;
  }

  .operators-control-table .v-input--selection-controls__input {
    margin: 0 auto;
  }

  .operators .v-data-iterator .v-data-footer__icons-after {
    margin-right: auto;
  }

  .operators-control-table td,
  .operators-control-table th {
    border-right: thin solid rgba(0, 0, 0, 0.12);
  }

  .operators-control-table td:first-child,
  .operators-control-table th:first-child {
    border-left: thin solid rgba(0, 0, 0, 0.12);
  }

  .operators-control-table tr:first-child th {
    border-top: thin solid rgba(0, 0, 0, 0.12);
  }

  .operators-control-table tr:first-child th:first-child {
    border-top-left-radius: 6px;
  }

  .operators-control-table tr:first-child th:last-child {
    border-top-right-radius: 6px;
  }

  .operators-control-table tr:last-child td:first-child {
    border-bottom-left-radius: 6px;
  }

  .operators-control-table tr:last-child td:last-child {
    border-bottom-right-radius: 6px;
  }

  .operators-control-table tr:last-child td {
    border-bottom: thin solid rgba(0, 0, 0, 0.12);
  }

  .operators-control-card .v-card__text {
    padding-bottom: 0;
  }
</style>
<script>
  import { debounce, keyBy, cloneDeep, groupBy } from 'lodash'
  import { API } from '@/utils/api'
  import { Operator, OperatorFormContainer, HistoryView, } from '@/modules/operators'
  import { DrawerDialog } from '../components/core/DrawerDialog'

  const USER_LIST = '/users/list.json'
  const USER_PERMISSIONS = '/users/permissions.json'
  const USER_UPDATE = '/users/update.json'
  const USER_ROLES = '/users/role/list.json'
  const ENTITY_LIST = '/users/entity_list.json'

  export default {
    data: () => ({
      searchTerm: '',
      isLoading: false,
      user: {
        dialog: false,
        info: false
      },
      users: [],
      entitiesById: {},
      permissionsByGroup: {},
      rolesById: {},
      baseProjectIdToSearch: 'ALL',
      footerProps: {
        'show-current-page': true,
        'show-first-last-page': true,
        'items-per-page-options': [ 10, 20, 50, 100 ]
      },

      showHistory: {
        for: null,
        type: '',
      },
    }),

    components: {
      Role: () => import('@/components/operators/Role'),
      Operator,
      OperatorFormContainer,
      DrawerDialog,
      HistoryView,
    },

    computed: {
      isPermissionEditingAllowed () {
        return this.$store.state.auth.checkAccess({ key: 'permissions_control', value: true })
      },
      canViewRoleHistory () {
        return this.$store.state.auth.checkAccess({ key: 'role_history', value: true })
      },
      canViewUserHistory () {
        return this.$store.state.auth.checkAccess({ key: 'user_history', value: true })
      },
      isTransactionLimitEditingAllowed () {
        return this.$store.state.auth.checkAccess({ key: 'manual_transaction_limits', value: true })
      },
      isPayoutLimitEditingAllowed () {
        return this.$store.state.auth.checkAccess({ key: 'payout_limits', value: true })
      },
      baseProjectFilterOptions () {
        return [
          {
            text: 'All',
            value: 'ALL'
          },
          {
            text: 'None',
            value: 'NONE'
          },
          ...Object.values(this.entitiesById)
        ]
      },

      historyDialogModel: {
        get() {
          return !!this.showHistory.for
        },
        set(value) {
          if (!value) {
            this.showHistory.for = null
          }
        },
      }
    },

    mounted () {
      this.initialize()
    },

    methods: {
      initialize () {
        this.isLoading = true

        return Promise.all([
          this.loadPermissionList(),
          this.loadRoles(),
          this.loadEntities(),
          this.loadUserList(),
        ]).finally(() => {
          this.isLoading = false
        })
      },

      searchFilter (users, searchTerm) {
        return users.filter(user => {
          if (this.baseProjectIdToSearch !== 'ALL') {
            const isMatchByBaseProject = this.getIsUserMatchByBaseProject(user, this.baseProjectIdToSearch)

            if (!isMatchByBaseProject) {
              return false
            }
          }

          if (searchTerm) {
            const isMatchBySearchTerm = this.getIsUserMatchBySearchTerm(user, searchTerm)

            if (!isMatchBySearchTerm) {
              return false
            }
          }

          return true
        })
      },

      getIsUserMatchBySearchTerm (user, searchValue) {
        const { name = '', login = '', user_id = '' } = user
        return [
          name, login, user_id
        ].some(searchTerm => searchTerm.toString().toLowerCase().includes(searchValue.toLowerCase()))
      },

      getIsUserMatchByBaseProject (user, baseProject) {
        if (baseProject === 'NONE') {
          return !user.base
        }

        if (baseProject > 0) {
          return user.base === baseProject
        }
      },

      onUpdatePermissions (operator, diff) {
        const updatedOperator = cloneDeep(operator)
        updatedOperator.permission = {
          ...updatedOperator.permission,
          ...diff
        }

        this.save(updatedOperator)
      },

      onUpdateProjects (operator, entities) {
        const updatedOperator = cloneDeep(operator)
        updatedOperator.entities = entities

        this.save(updatedOperator)
      },

      onUpdateStatus (operator, status) {
        const updatedOperator = cloneDeep(operator)
        updatedOperator.status = status

        this.save(updatedOperator)
      },

      onAddUserClick () {
        this.openUser({
          new: true,
          permission: {},
          notifications: {},
          entities: [],
          password: '',
          payout_limits: {},
        })
      },
      modifyPermissionResponse (permissionsByKey) {
        for (const key in permissionsByKey) {
          permissionsByKey[key].key = key
          permissionsByKey[key].group = permissionsByKey[key].group ?? 'general'
        }
      },
      openUser (item) {
        this.user.info = item
        this.user.dialog = true
      },
      async save (item) {
        this.isLoading = true
        if (!await this.$root.$confirm('Confirm', `Update user: ${item.login} (${item.name})`)) {
          this.$nextTick(() => {
            this.isLoading = false
          })
          return
        }

        if ('role_id' in item && item.role_id == undefined) {
          item.role_id = -1
        }

        try {
          await API.call(USER_UPDATE, item)
          await this.loadUserList()

          this.$notification.message = 'Operator updated'
          this.$notification.type = 'success'
        } finally {
          this.$notification.show = true

          this.user.dialog = false
          this.isLoading = false
        }
      },
      updateSearch: debounce(function (value) {
        this.searchTerm = value
      }, 300),

      loadUserList () {
        this.isLoading = true
        return API.call(USER_LIST, { with_base: 1 }).then(r => this.users = r.data).finally(() => {
          this.isLoading = false
        })
      },

      loadPermissionList () {
        return API.call(USER_PERMISSIONS).then(r => {
          this.modifyPermissionResponse(r.data)
          this.permissionsByGroup = groupBy(r.data, 'group')
        })
      },

      loadRoles () {
        return API.call(USER_ROLES).then(r => this.rolesById = keyBy(r.data, 'role_id'))
      },

      loadEntities () {
        return API.call(ENTITY_LIST, { with_base: 1 }).then(r => this.entitiesById = keyBy(r.data, 'value'))
      }
    }
  }
</script>
